/**
 * useSubmitMessage — 消息提交逻辑（首问 + 续问流程）
 * 从 CustomerSuggestion 中抽取
 */
import { useCallback, useRef, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";

import {
  isHistoryConversation,
  normalizeReferences,
  mergeReferenceEventMetadata,
  buildDocumentsFromReferences,
  getReferenceRelatedDocIds,
  matchDocInfoByRelatedIds,
  getDocInfoDocumentGuid,
  getDocInfoDataSource,
  getDocInfoCategoryIds,
  getDocInfoSourcePathAskId,
  isDocInfoValid,
} from "@/utils/conversation";
import { splitReferencesFence } from "@/utils/referencesFence";
import { formatFileSize } from "@/utils/formatters";
import {
  parseFinalAnswer,
  extractStreamingAnswerView,
  createAnswerStreamFilter,
} from "@/utils/answer";
import { createStreamTypewriter } from "@/utils/streamTypewriter";
import { createFrameThrottle } from "@/utils/frameThrottle";
import { isStreamSmoothingEnabled } from "@/utils/streamDisplayConfig";
import { createSessionLogStream, searchSessionLog } from "@/services/opsmate/sessionLog";
import { createConQuestion } from "@/services/opsmate/conQuestion";
import { createConReply } from "@/services/opsmate/conReply";
import { saveBulkCreateConCitation } from "@/services/opsmate/conCitation";
import { getDocumentsByIds } from "@/services/opsmate/documents";
import { createConversation, generateConversationTitle } from "@/services/opsmate/conversation";
import {
  buildCreateSessionLogParams,
  buildQuestionScopeParams,
  canSubmitQuestionScope,
} from "./useSubmitMessage.scope";

const INCLUDE_HISTORY_LOGS = true;

// 提问内容最大字符数，需与后端 SessionLog.Question 限制保持一致
export const MAX_QUESTION_LENGTH = 2000;

interface UseSubmitMessageParams {
  items: any[];
  setItemsForConversation: (convId: string, updater: React.SetStateAction<any[]>) => void;
  refreshConversations: () => Promise<void>;
  setSelectedKeys: React.Dispatch<React.SetStateAction<string>>;
  selectedKeys: string;
  onlyUseSelectedSources: boolean;
  pendingConversationId: string | null;
  setPendingConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  migrateNewConversationSources: (conversationId: string) => void;
  selectedSourceGuidNames: string[];
  /**
   * General Docs 胶囊本次生效的分类 ID。空数组表示胶囊关闭（不检索 General 来源）。
   * 服务端仍会与用户授权取交集，此处只负责传达用户的收窄意图。
   */
  docCategoryIds: string[];
  /** Study Source 胶囊本次生效的 SourcePathStudy.ID；空数组表示胶囊关闭。 */
  studySourceIds: string[];
  /** Personal Docs 胶囊是否显式勾选。 */
  includePersonalDocs: boolean;
}

export function useSubmitMessage({
  items,
  setItemsForConversation,
  refreshConversations,
  setSelectedKeys,
  selectedKeys,
  onlyUseSelectedSources,
  setPendingConversationId,
  migrateNewConversationSources,
  selectedSourceGuidNames,
  docCategoryIds,
  studySourceIds,
  includePersonalDocs,
}: UseSubmitMessageParams) {
  const { message } = App.useApp();
  const { t } = useTranslation();

  // 按会话维度独立维护输入与提交状态，避免话题之间互相影响
  const [valueByConv, setValueByConv] = useState<Record<string, string>>({});
  const [submittingByConv, setSubmittingByConv] = useState<Record<string, boolean>>({});
  const valueByConvRef = useRef(valueByConv);
  valueByConvRef.current = valueByConv;
  // 既用于发起 stop，也用作"该话题正在提交中"的标记，存在即视为 in-flight
  const abortControllersByConv = useRef<Map<string, AbortController>>(new Map());

  const [conversationId, setConversationId] = useState<string | null>(null);
  const inputRef = useRef<any>(null);
  const lastReferencesRef = useRef<any[]>([]);

  // 当前话题对应的派生值
  const value = valueByConv[selectedKeys] ?? "";
  const submitting = !!submittingByConv[selectedKeys];

  const setValue = useCallback(
    (next: string) => {
      setValueByConv((prev) => ({ ...prev, [selectedKeys]: next }));
    },
    [selectedKeys],
  );

  // 中止当前话题的流式生成（其它话题不受影响）
  const stopGeneration = useCallback(() => {
    const ctrl = abortControllersByConv.current.get(selectedKeys);
    if (ctrl) ctrl.abort();
  }, [selectedKeys]);

  const handleSubmit = useCallback(async () => {
    const trimmedValue = (valueByConvRef.current[selectedKeys] ?? "").trim();
    if (!trimmedValue) return;
    if (!canSubmitQuestionScope(onlyUseSelectedSources, selectedSourceGuidNames)) return;

    if (trimmedValue.length > MAX_QUESTION_LENGTH) {
      message.warning(t("opsmate.suggestion.questionTooLong", { max: MAX_QUESTION_LENGTH }));
      return;
    }

    // 当前话题对应的提交标识：历史会话用其 ID，否则使用已记录的 conversationId 或 "add"
    const initialKey = isHistoryConversation(selectedKeys)
      ? String(selectedKeys)
      : conversationId || "add";

    // 同一话题禁止重复提交，但不阻止其它话题
    if (abortControllersByConv.current.has(initialKey)) return;

    // 用 UTC 生成时间字符串，与后端 DateTime.UtcNow 落库保持一致。
    // （后端 Create 已统一用 UTC 兜底；此处同样发 UTC，避免请求体里出现本地时间造成歧义。）
    const formatNow = () => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    };

    const now = Date.now();
    const userKey = `user-${now}`;
    const aiKey = `ai-${now}`;

    const userMessage = { key: userKey, role: "user", content: trimmedValue };
    const loadingAiMessage = {
      key: aiKey,
      role: "ai",
      status: "loading",
      loading: true,
      content: "",
    };

    // 提前创建 AbortController：作为该话题"提交中"的标识，并支持立即响应停止按钮
    const abortController = new AbortController();
    let submissionKey = initialKey;
    abortControllersByConv.current.set(submissionKey, abortController);
    setSubmittingByConv((prev) => ({ ...prev, [submissionKey]: true }));
    // 清空当前话题输入框（仅当前话题，不影响其它）
    setValueByConv((prev) => ({ ...prev, [submissionKey]: "" }));

    // 目标会话更新器；activeConversationId 确定后在 try 内重新赋值，catch 也可访问
    let setActiveItems: (updater: React.SetStateAction<any[]>) => void = (updater) =>
      setItemsForConversation(submissionKey, updater);

    try {
      const selectedConversationId = isHistoryConversation(selectedKeys)
        ? String(selectedKeys)
        : null;

      let activeConversationId = selectedConversationId || conversationId;
      const createdNewConversation = !activeConversationId;

      if (!activeConversationId) {
        const t = formatNow();
        const createConvResp = await createConversation({
          CreateTime: t,
          UpdateTime: t,
          ConversationStatus: "active",
          LastActiveTime: t,
          ConversationTitle: trimmedValue,
        });
        const convRaw = (createConvResp as any) ?? {};
        const convEntity = (convRaw.Entity as any) || convRaw;
        const createdId =
          convEntity?.ConversationID ??
          convEntity?.conversationId ??
          convEntity?.ID ??
          convEntity?.Id ??
          convEntity?.id ??
          convRaw?.ConversationID ??
          convRaw?.conversationId ??
          convRaw?.ID ??
          convRaw?.Id ??
          convRaw?.id ??
          null;
        if (!createdId) throw new Error("createConversation did not return ConversationID");
        activeConversationId = String(createdId);
        setConversationId(activeConversationId);
      }

      // 新建会话时，将提交状态从 "add" 迁移到真实会话 ID（保持流式期间的状态键与会话一致）
      if (submissionKey !== activeConversationId) {
        const fromKey = submissionKey;
        const toKey = activeConversationId as string;
        abortControllersByConv.current.delete(fromKey);
        abortControllersByConv.current.set(toKey, abortController);
        setSubmittingByConv((prev) => {
          const next = { ...prev };
          delete next[fromKey];
          next[toKey] = true;
          return next;
        });
        submissionKey = toKey;
      }

      if (createdNewConversation) {
        try {
          setPendingConversationId?.(String(activeConversationId));
          await refreshConversations?.();
          if (typeof setSelectedKeys === "function") {
            migrateNewConversationSources(String(activeConversationId));
            setSelectedKeys(String(activeConversationId));
          }
        } catch (e) {
          console.error("refreshConversations after createConversation failed", e);
        }
      }

      // 锁定本次提交的目标会话，防止 selectedKeys 切换后流式回调写入错误的 items 槽位
      setActiveItems = (updater) =>
        setItemsForConversation(activeConversationId as string, updater);

      setActiveItems((prev: any[]) => [...prev, userMessage, loadingAiMessage]);

      try {
        await createConQuestion({
          content: trimmedValue,
          conversationId: activeConversationId,
        });
      } catch (e) {
        console.error("conquestion 调用失败:", e);
      }

      let historySessionLogIdList: string[] = [];
      if (!createdNewConversation && INCLUDE_HISTORY_LOGS && activeConversationId) {
        try {
          const historyResp: any = await searchSessionLog({
            ConversationID: String(activeConversationId),
            Page: 1,
            Limit: 100,
          });
          const rawList: any[] = historyResp.Data;
          historySessionLogIdList = rawList
            .filter((row: any) => String(row.SessionLog_QuestionType) === "0")
            .map((row: any) => String(row.ID ?? ""))
            .filter((id: string) => !!id)
            .slice(0, 100);
        } catch (e) {
          console.error("searchSessionLog for HistorySessionLogIdList failed:", e);
          historySessionLogIdList = [];
        }
      }

      const questionScopeParams = buildQuestionScopeParams({
        onlyUseSelectedSources,
        selectedSourceGuidNames,
        docCategoryIds,
        studySourceIds,
        includePersonalDocs,
      });

      const sessionLogParams = buildCreateSessionLogParams({
        question: trimmedValue,
        conversationId: activeConversationId,
        historySessionLogIdList: INCLUDE_HISTORY_LOGS ? historySessionLogIdList : [],
        questionScopeParams,
      });

      // ==================== 流式调用（PikeRag v2 SSE） ====================
      let streamedAnswer = "";
      let finalAnswerFromEvent = "";
      // AI Reasoning 区域读取 rationale 内容
      let rationaleText = "";
      // Summary 区域当前无单独来源，保持为空（V3 不产出独立 summary 字段）
      const summaryText = "";
      // references = 最终引用清单；eventReferences 仅缓存 `event: references` 的元数据，
      // 待流结束后与内联 ```references 解析出的清单合并（内联块建表，事件补元数据）。
      let references: any[] = [];
      let eventReferences: any[] = [];
      let sessionLogIdFromResp: string | undefined;
      let streamError: unknown = null;
      let finalExecutionCode: number | null = null;
      let finalExecutionMessage = "";

      // 累积的推理 / 检索过程步骤（type=reasoning / tool）
      const progressSteps: any[] = [];

      // 注意：模型可能把答案以 JSON 字符串（含 answer / rationale）形式输出，
      // 通过 createAnswerStreamFilter 在喂入打字机前先做"二级队列"过滤：
      //   - plain 文本：原样转发
      //   - JSON 包装：只把 "answer" 字段的内层字符按 delta 推入打字机，
      //     保证打字机仍按帧节奏渲染，而不是越过 `"answer":"` 边界后一次性蹦出整段。
      // 显示层平滑（打字机 + 帧节流）总开关，默认关：按上游节奏原速渲染。
      const smoothing = isStreamSmoothingEnabled();
      const answerFilter = createAnswerStreamFilter();
      const typewriter = createStreamTypewriter({
        enabled: smoothing,
        onTick: (visible) => {
          // 流式途中即剥离内联 ```references 围栏，避免把 JSON 原文显示在回答里；
          // 围栏尚未闭合时打 buildingReferences 标记，并把“已攒够的完整引用条目”逐条
          // 解析出来增量建表（不等整段 JSON、不等 getdocumentid），渲染层据此边构建边显示。
          const split = splitReferencesFence(visible);
          const previewRefs =
            split.building || split.references.length > 0
              ? normalizeReferences(split.references)
              : [];
          setActiveItems((prev: any[]) =>
            prev.map((item: any) =>
              item.key === aiKey
                ? {
                    ...item,
                    status: "streaming",
                    loading: false,
                    content: split.text,
                    buildingReferences: split.building,
                    references: previewRefs,
                    documents: buildDocumentsFromReferences(previewRefs),
                    progress: [...progressSteps],
                  }
                : item,
            ),
          );
        },
      });

      const appendStreamDelta = (delta: string) => {
        for (const piece of answerFilter.push(delta)) typewriter.push(piece);
        streamedAnswer = answerFilter.getRawBuffer();
      };

      // 将 reasoning / tool 事件累积进 progressSteps；用 frame 节流批量刷新气泡，
      // 避免 SSE 每个事件触发一次 setState 引起卡顿
      const flushProgress = createFrameThrottle(() => {
        const snapshot = [...progressSteps];
        setActiveItems((prev: any[]) =>
          prev.map((item: any) =>
            item.key === aiKey
              ? {
                  ...item,
                  status: item.status === "loading" ? "streaming" : item.status,
                  loading: false,
                  progress: snapshot,
                }
              : item,
          ),
        );
      }, smoothing);

      // 当前正在累积的 llm.delta reasoning 步骤的打字机；非同块事件出现时需 flush
      let reasoningTypewriter: ReturnType<typeof createStreamTypewriter> | null = null;
      let reasoningTypewriterStep: any = null;

      const closeReasoningTypewriter = () => {
        if (reasoningTypewriter) {
          reasoningTypewriter.flush();
          reasoningTypewriter = null;
          reasoningTypewriterStep = null;
        }
      };

      const pushProgressStep = (kind: "reasoning" | "tool", evt: any) => {
        const event = String(evt?.event ?? "");
        const reasoningDelta = typeof evt?.reasoning === "string" ? evt.reasoning : "";

        // 对于 LLM reasoning 增量（llm.delta），合并到最后一条同类步骤中
        if (
          kind === "reasoning" &&
          event === "llm.delta" &&
          progressSteps.length > 0 &&
          progressSteps[progressSteps.length - 1].kind === "reasoning" &&
          progressSteps[progressSteps.length - 1].event === "llm.delta" &&
          reasoningTypewriterStep === progressSteps[progressSteps.length - 1] &&
          reasoningTypewriter
        ) {
          const last = progressSteps[progressSteps.length - 1];
          last.updatedAt = Date.now();
          reasoningTypewriter.push(reasoningDelta);
        } else {
          // 切换到新步骤前，先把上一个 reasoning 打字机收尾
          closeReasoningTypewriter();
          const step: any = {
            kind,
            event,
            phase: evt?.phase ?? "",
            status: evt?.status ?? "",
            tool: evt?.tool ?? "",
            reasoning: "",
            payload: evt?.payload ?? null,
            raw: evt,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          progressSteps.push(step);

          if (kind === "reasoning" && event === "llm.delta") {
            reasoningTypewriterStep = step;
            reasoningTypewriter = createStreamTypewriter({
              enabled: smoothing,
              onTick: (visible) => {
                step.reasoning = visible;
                step.updatedAt = Date.now();
                flushProgress.schedule();
              },
            });
            reasoningTypewriter.push(reasoningDelta);
          } else {
            step.reasoning = reasoningDelta;
          }
        }

        flushProgress.schedule();
      };

      // status（阶段提示）/ progress（进度提示）属于结构化步骤，
      // 不走打字机，直接作为独立 step 追加；追加前先把上一个 reasoning 打字机收尾。
      const pushStructuredStep = (partial: Record<string, any>) => {
        closeReasoningTypewriter();
        progressSteps.push({
          ...partial,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
        flushProgress.schedule();
      };

      await createSessionLogStream(
        {
          ...sessionLogParams,
        },
        {
          signal: abortController.signal,
          onContent: (delta) => appendStreamDelta(delta),
          onReasoning: (evt) => pushProgressStep("reasoning", evt),
          onTool: (evt) => pushProgressStep("tool", evt),
          onStatus: (evt) => {
            const message = String(evt?.message ?? "");
            pushStructuredStep({ kind: "status", event: "status", message });
          },
          // progress（“正在加载/组装/调用/执行工具…”）不展示。
          // trace 仅保留“带命中文档的检索结果”（kb_search 等 tool_result），用于列出引用的文档名称；
          // 其余 trace（tool_call / load_skill / 纯文本结果等）不展示。
          onTrace: (evt) => {
            const trace = evt?.trace ?? {};
            if (String(trace?.role) !== "tool_result" || !Array.isArray(trace?.toolResult)) return;
            const hasDocName = trace.toolResult.some(
              (r: any) => typeof r?.filename === "string" && r.filename.trim(),
            );
            if (!hasDocName) return;
            pushStructuredStep({ kind: "trace", event: "trace", trace });
          },
          onReferences: (evt) => {
            // 上游原生字段（documentId/segment...）归一化后暂存，流结束再与内联块合并补全
            eventReferences = normalizeReferences(evt?.references);
          },
          // 完成事件：status=failed / finishReason=error 视为工作流失败
          onCompleted: (evt) => {
            const status = String(evt?.status ?? "");
            const finishReason = String(evt?.finishReason ?? "");
            if (status === "failed" || finishReason === "error") {
              if (finalExecutionCode === null || finalExecutionCode === 0) finalExecutionCode = 1;
            }
          },
          // 错误事件（上游 stream.error 或控制器层 4xx/5xx）
          onStreamError: (err) => {
            const codeNum = Number(err?.code);
            finalExecutionCode = Number.isFinite(codeNum) && codeNum !== 0 ? codeNum : 1;
            if (typeof err?.message === "string" && err.message) {
              finalExecutionMessage = err.message;
            }
          },
          onAnswerSaved: (evt) => {
            sessionLogIdFromResp = evt?.session_log_id;
            if (evt && typeof evt.execution_code !== "undefined") {
              const codeNum = Number(evt.execution_code);
              finalExecutionCode = Number.isFinite(codeNum) ? codeNum : null;
            }
            if (typeof evt?.execution_message === "string") {
              finalExecutionMessage = evt.execution_message;
            }
            // 失败时后端会带回兜底答案文本；成功时为空，由流式累积内容兜底
            if (typeof evt?.answer === "string" && evt.answer) {
              finalAnswerFromEvent = evt.answer;
            }
          },
          onError: (err) => {
            streamError = err;
          },
        },
      );

      // abort 时放弃队列剩余；正常结束时先让过滤器把余量推入打字机，再一次性吐完
      if (abortController.signal.aborted) {
        typewriter.cancel();
        reasoningTypewriter?.cancel();
        reasoningTypewriter = null;
        reasoningTypewriterStep = null;
        flushProgress.cancel();
      } else {
        for (const piece of answerFilter.flush()) typewriter.push(piece);
        typewriter.flush();
        streamedAnswer = answerFilter.getRawBuffer();
        closeReasoningTypewriter();
        flushProgress.flush();
      }

      if (streamError) throw streamError;

      // 工作流失败（execution_code 非 0）时，回退为错误文案作为回答内容展示
      if (
        finalExecutionCode !== null &&
        finalExecutionCode !== 0 &&
        !streamedAnswer &&
        !finalAnswerFromEvent
      ) {
        let failureMsg = finalExecutionMessage || "工作流执行失败";
        try {
          const parsed = JSON.parse(finalExecutionMessage);
          if (parsed && typeof parsed === "object") {
            failureMsg = (parsed as any).message || (parsed as any).code || finalExecutionMessage;
          }
        } catch {
          /* ignore: execution_message 非 JSON 时按原文展示 */
        }
        finalAnswerFromEvent = `[请求失败 ${finalExecutionCode}] ${failureMsg}`;
      }

      // 兜底解析：再尝试从 streamedAnswer 中拆出 rationale 供 AI Reasoning 展示
      const parsedFromStream = parseFinalAnswer(streamedAnswer);
      if (parsedFromStream && !rationaleText && parsedFromStream.rationale) {
        rationaleText = parsedFromStream.rationale;
      }

      // 计算最终展示文本：
      //   优先使用失败兜底 finalAnswerFromEvent（answer_saved 在失败时下发，
      //   或上面据 execution_code 拼出的错误文案；通过 extractStreamingAnswerView 再清洗一次），
      //   否则退回到流式累积的 streamedAnswer 经清洗后的视图。
      //   先 splitReferencesFence 剥离内联 ```references 围栏（并解析内联引用清单），
      //   再 extractStreamingAnswerView 去 JSON 外壳——顺序不能反：否则 ```references
      //   会被去壳逻辑误当成 JSON 代码围栏，剥壳后找不到 answer 字段而返回空，
      //   导致兜底链一路退回带围栏原文的 streamedAnswer，把 JSON 原样显示出来。
      const finalSplit = splitReferencesFence(finalAnswerFromEvent);
      const streamSplit = splitReferencesFence(streamedAnswer);
      const cleanedFromFinal = extractStreamingAnswerView(finalSplit.text);
      const cleanedFromStream = extractStreamingAnswerView(streamSplit.text);
      let answerText =
        cleanedFromFinal ||
        cleanedFromStream ||
        finalSplit.text ||
        streamSplit.text ||
        "已提交，请等待回复";

      // 内联块建表，事件补元数据：内联 ```references 为权威清单，event: references 补全 segment 等
      const inlineReferences = normalizeReferences(streamSplit.references);
      references = inlineReferences.length
        ? mergeReferenceEventMetadata(inlineReferences, eventReferences)
        : eventReferences;

      if (abortController.signal.aborted) {
        const cancelText = "Okay, I've stopped responding.";
        setActiveItems((prev: any[]) =>
          prev.map((item: any) =>
            item.key === aiKey
              ? { ...item, status: "success", loading: false, content: cancelText }
              : item,
          ),
        );
        setPendingConversationId?.(null);
        return;
      }
      lastReferencesRef.current = Array.isArray(references) ? references : [];

      const docMap = new Map<
        string,
        {
          id?: string;
          name: string;
          citations: string;
          canDownload: boolean;
          size?: string;
          uploadedAt?: string;
          segmentNums: number[];
          /** 该引用关联的全部文档 ID（relatedDocumentIds，含 owner 与重复件），用于命中 DB 文档 */
          relatedIds: string[];
          /** 是否在 DB DocumentList 中存在；仅 true 的文档展示到右侧边栏 */
          inDocumentList?: boolean;
        }
      >();
      const refDocIdSet = new Set<string>();

      (references || []).forEach((ref: any, idx: number) => {
        const segmentNum = idx + 1;
        const id = ref?.references_document_id ?? ref?.document_id ?? "";
        const refName = (ref?.references_document_name ?? id ?? "").toString().trim();
        if (!id && !refName) return;
        const key = id || refName;
        // 用 relatedDocumentIds 查 getdocumentid：owner 未必落库，重复件才在本租户 DB 里。
        const relatedIds = getReferenceRelatedDocIds(ref);
        relatedIds.forEach((rid) => refDocIdSet.add(rid));

        const rawSegment = (ref?.references_segment ?? "").toString();
        let seg = rawSegment;
        const marker = "Content: ";
        const markerIndex = rawSegment.indexOf(marker);
        if (markerIndex >= 0) seg = rawSegment.slice(markerIndex + marker.length);

        const existing = docMap.get(key);
        if (!existing) {
          docMap.set(key, {
            id,
            name: refName || id,
            citations: seg || "",
            canDownload: true,
            segmentNums: [segmentNum],
            relatedIds,
          });
          return;
        }
        existing.segmentNums.push(segmentNum);
        relatedIds.forEach((rid) => {
          if (!existing.relatedIds.includes(rid)) existing.relatedIds.push(rid);
        });
        if (seg) {
          existing.citations = existing.citations ? `${existing.citations}\n\n---\n\n${seg}` : seg;
        }
        docMap.set(key, existing);
      });

      let documents = Array.from(docMap.values());

      const enrichDocumentsByIds = async () => {
        try {
          const docIdArray = Array.from(refDocIdSet);
          if (docIdArray.length === 0) return;
          const docInfoResp: any = await getDocumentsByIds(docIdArray);
          const docInfoList: any[] =
            docInfoResp && Array.isArray(docInfoResp.data)
              ? docInfoResp.data
              : Array.isArray(docInfoResp)
                ? docInfoResp
                : [];

          documents = Array.from(docMap.entries()).map(([_key, doc]) => {
            const matchedInfo = matchDocInfoByRelatedIds(docInfoList, doc.relatedIds, doc.name);
            // 不在 DB DocumentList 中：仍保留在 documents（正文角标 / References 区需要展示），
            // 但不打 inDocumentList 标记，右侧边栏据此过滤
            if (!matchedInfo) return doc;

            const rawSize = matchedInfo.FileSize ?? matchedInfo.fileSize ?? matchedInfo.size;
            const size = formatFileSize(rawSize);
            const uploadedAt =
              matchedInfo.CreateTime ??
              matchedInfo.FileCreateTime ??
              matchedInfo.FileUpdateTime ??
              matchedInfo.uploadTime ??
              matchedInfo.uploadedAt ??
              matchedInfo.createTime;
            const fileUrl = matchedInfo.DownloadURL;
            const displayName =
              matchedInfo.FileName ??
              matchedInfo.fileName ??
              matchedInfo.documentName ??
              matchedInfo.DocumentName ??
              doc.name;

            // 右侧文档清单完全以 getdocumentid 校准回的 DocumentGuid 为准：写入 guidName
            // （getDocumentGuid 优先取它），用于去重与 Only use selected sources 的 references_document_id。
            // 保留 doc.id（上游 owner 文档 ID）不动：正文角标命中与 artifact 图片鉴权仍按 owner ID。
            return {
              ...doc,
              guidName: getDocInfoDocumentGuid(matchedInfo) || undefined,
              sourcePathAskId: getDocInfoSourcePathAskId(matchedInfo) || undefined,
              docCategoryIds: getDocInfoCategoryIds(matchedInfo),
              name: displayName ? String(displayName) : doc.name,
              size: size || undefined,
              uploadedAt: uploadedAt ? String(uploadedAt) : undefined,
              fileUrl: fileUrl ? String(fileUrl) : undefined,
              dataSource: getDocInfoDataSource(matchedInfo) || undefined,
              // 软删旧行（IsValid=false）仍保留 enrichment 供下载/References，但不进右侧边栏
              inDocumentList: isDocInfoValid(matchedInfo),
            };
          });
        } catch (e) {
          console.error("getDocumentsByIds 调用失败:", e);
        }
      };

      // activeConversationId 已在流程前创建/确认；answerSessionLog 的 ID 从 onAnswerSaved 获取
      if (activeConversationId) setConversationId(String(activeConversationId));
      const questionIdFromResp = sessionLogIdFromResp;

      // 续问流程
      if (!createdNewConversation) {
        let replyIdFromResp: string | undefined;
        try {
          const replyResp = await createConReply({
            questionId: sessionLogIdFromResp,
            content: answerText ?? "",
          });
          const replyRaw = (replyResp as any) ?? {};
          const replyEntity = (replyRaw.Entity as any) || (replyRaw.entity as any) || replyRaw;
          const replyId =
            replyEntity?.ID ??
            replyEntity?.Id ??
            replyEntity?.id ??
            replyRaw?.ID ??
            replyRaw?.Id ??
            replyRaw?.id ??
            undefined;
          if (replyId) replyIdFromResp = String(replyId);
        } catch (e) {
          console.error("conreply 调用失败:", e);
        }

        await enrichDocumentsByIds();

        if (replyIdFromResp && Array.isArray(references) && references.length > 0) {
          try {
            const bulkItems = references
              .map((ref: any) => {
                const citationId = ref?.references_segment_id ?? ref?.segment_id ?? "";
                const citationContent = ref?.references_segment ?? ref?.segment_content ?? "";
                const documentId = ref?.references_document_id ?? ref?.document_id ?? "";
                const position = ref?.references_segment_position ?? ref?.segment_position ?? "";
                // v2 不返回 segment 正文时，content 为空字符串；仍保留引用关联
                if (!citationId) return null;
                return {
                  replyId: String(replyIdFromResp),
                  citationId: String(citationId),
                  content: String(citationContent ?? ""),
                  documentId: documentId ? String(documentId) : undefined,
                  position: position ? String(position) : undefined,
                };
              })
              .filter(Boolean) as any[];
            if (bulkItems.length > 0) await saveBulkCreateConCitation(bulkItems);
          } catch (e) {
            console.error("concitation(savebulkcreate) 调用失败:", e);
          }
        }

        setActiveItems((prev: any[]) =>
          prev.map((item: any) =>
            item.key === aiKey
              ? {
                  ...item,
                  status: "success",
                  loading: false,
                  content: answerText || " ",
                  summary: summaryText ?? "",
                  rationale: rationaleText ?? "",
                  documents,
                  references,
                  progress: progressSteps.length > 0 ? [...progressSteps] : item.progress,
                  extraInfo: {
                    ...(item.extraInfo || {}),
                    sessionLogId: sessionLogIdFromResp ? String(sessionLogIdFromResp) : undefined,
                    feedback: (item.extraInfo?.feedback as any) ?? "default",
                  },
                }
              : item,
          ),
        );
        return;
      }

      // 首问流程
      await enrichDocumentsByIds();

      setActiveItems((prev: any[]) =>
        prev.map((item: any) =>
          item.key === aiKey
            ? {
                ...item,
                status: "success",
                loading: false,
                content: answerText || " ",
                summary: summaryText ?? "",
                rationale: rationaleText ?? "",
                documents,
                references,
                progress: progressSteps.length > 0 ? [...progressSteps] : item.progress,
                extraInfo: {
                  ...(item.extraInfo || {}),
                  sessionLogId: sessionLogIdFromResp ? String(sessionLogIdFromResp) : undefined,
                  feedback: (item.extraInfo?.feedback as any) ?? "default",
                },
              }
            : item,
        ),
      );

      // 用函数式更新读取“当前”state 来清除：闭包里的 pendingConversationId 是本次提交开始时
      // 定格的旧值（新建会话时通常为 null），若用它做判断会导致本次设置的 pending 永远清不掉，
      // 残留值会污染“下一次”提交里被捕获的 refreshConversations 闭包，使二次刷新把选中改回旧会话。
      setPendingConversationId?.((prev) =>
        String(prev) === String(activeConversationId) ? null : prev,
      );

      try {
        // 与 conreply/create 并发：用 GPT 总结首次问答并为会话生成简短标题、更新会话表。
        // 失败已在后端/此处静默吞掉，不影响主流程；待其完成后再刷新左侧会话列表以显示新标题。
        const titlePromise =
          createdNewConversation && activeConversationId
            ? generateConversationTitle(String(activeConversationId)).catch((e) => {
                console.error("generateConversationTitle 调用失败:", e);
              })
            : null;

        const replyResp = await createConReply({
          questionId: questionIdFromResp,
          content: answerText ?? "",
        });
        try {
          const replyRaw = (replyResp as any) ?? {};
          const replyEntity = (replyRaw.Entity as any) || (replyRaw.entity as any) || replyRaw;
          const replyIdFromResp =
            replyEntity?.ID ??
            replyEntity?.Id ??
            replyEntity?.id ??
            replyRaw?.ID ??
            replyRaw?.Id ??
            replyRaw?.id ??
            undefined;

          if (replyIdFromResp && Array.isArray(references) && references.length > 0) {
            const replyIdStr = String(replyIdFromResp);
            const bulkItems = references
              .map((ref: any) => {
                const citationId = ref?.references_segment_id ?? ref?.segment_id ?? "";
                const citationContent = ref?.references_segment ?? ref?.segment_content ?? "";
                const documentId = ref?.references_document_id ?? ref?.document_id ?? "";
                const position = ref?.references_segment_position ?? ref?.segment_position ?? "";
                // v2 不返回 segment 正文时，content 为空字符串；仍保留引用关联
                if (!citationId) return null;
                return {
                  replyId: replyIdStr,
                  citationId: String(citationId),
                  content: String(citationContent ?? ""),
                  documentId: documentId ? String(documentId) : undefined,
                  position: position ? String(position) : undefined,
                };
              })
              .filter(Boolean) as any[];
            if (bulkItems.length > 0) await saveBulkCreateConCitation(bulkItems);
          }
        } catch (e) {
          console.error("concitation(savebulkcreate) 调用失败:", e);
        }

        if (createdNewConversation && activeConversationId) {
          // 等标题生成落库后再刷新列表，确保左侧会话列表直接显示 GPT 生成的新标题
          if (titlePromise) await titlePromise;
          try {
            await refreshConversations?.();
          } catch (e) {
            console.error("refreshConversations after createConversation failed", e);
          }
          // 二次刷新后重新锁定到本次新建会话：刷新内部的“pending 自动选中”可能因捕获了
          // 旧的 pendingConversationId 而把选中改到旧会话（表现为“跳到第二个”并切换右侧内容）。
          if (typeof setSelectedKeys === "function") {
            setSelectedKeys(String(activeConversationId));
          }
        }
      } catch (e) {
        console.error("conreply 调用失败:", e);
      }
    } catch (error) {
      // 用户主动取消时不显示错误
      if (error instanceof DOMException && error.name === "AbortError") {
        const answerText = "Okay, I've stopped responding.";
        setActiveItems((prev: any[]) =>
          prev.map((item: any) =>
            item.key === aiKey
              ? {
                  ...item,
                  status: "success",
                  loading: false,
                  content: answerText,
                }
              : item,
          ),
        );
        setPendingConversationId?.(null);
        return;
      }
      message.error(t("opsmate.suggestion.sendFailed"));
      setActiveItems((prev: any[]) =>
        prev.map((item: any) =>
          item.key === aiKey
            ? {
                ...item,
                status: "error",
                loading: false,
                content: t("opsmate.suggestion.sendFailedContent"),
              }
            : item,
        ),
      );
    } finally {
      abortControllersByConv.current.delete(submissionKey);
      setSubmittingByConv((prev) => {
        if (!prev[submissionKey]) return prev;
        const next = { ...prev };
        delete next[submissionKey];
        return next;
      });
      setTimeout(() => {
        if (inputRef.current) {
          const textarea = inputRef.current.querySelector("textarea");
          if (textarea) textarea.focus();
        }
      }, 100);
    }
  }, [
    selectedKeys,
    conversationId,
    onlyUseSelectedSources,
    selectedSourceGuidNames,
    docCategoryIds,
    studySourceIds,
    includePersonalDocs,
    setItemsForConversation,
    refreshConversations,
    setSelectedKeys,
    setPendingConversationId,
    migrateNewConversationSources,
    message,
    t,
  ]);

  return {
    value,
    setValue,
    submitting,
    conversationId,
    setConversationId,
    inputRef,
    stopGeneration,
    handleSubmit,
  };
}
